import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import { Smartphone, Key } from 'lucide-react'
import { Logo } from '@/components/Logo'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useMfaChallenge } from '@/features/auth/hooks/useAuth'
import { useMfaMethods as useMfaMethodsStore, useUser, useAuthStore } from '@/app/store/auth'
import { LanguageSwitcher } from '@/components/LanguageSwitcher'
import { cn } from '@/lib/utils'
import { TotpSetupPage } from './TotpSetupPage'
import { WebAuthnSetupPage } from './WebAuthnSetupPage'
import { 
    EnterpriseWebAuthnAPI, 
    authenticateEnterprisePasskey,
    getWebAuthnErrorMessage,
    isWebAuthnSupported 
} from '@/lib/security/webauthn-enterprise'
import { useToast } from '@/hooks/use-toast'
import { useNavigate } from 'react-router-dom'

export function MfaPage() {
    const { t } = useTranslation()
    const { mfaNeedsSetup, mfaMethods } = useAuthStore((state) => ({
        mfaNeedsSetup: state.mfaNeedsSetup,
        mfaMethods: state.mfaMethods || []
    }))
    const user = useUser()
    
    // Determine available methods and default selection
    const hasTotp = mfaMethods.includes('totp') || user?.mfa?.totpEnabled
    const hasWebAuthn = mfaMethods.includes('webauthn') || user?.mfa?.webauthnEnabled || user?.webauthnEnabled
    
    // Default to Passkey (webauthn) if both are enabled, otherwise use the only available one
    const defaultMethod = hasWebAuthn ? 'webauthn' : 'totp'
    const [selectedMethod, setSelectedMethod] = useState<'totp' | 'webauthn'>(defaultMethod)
    const [isWebAuthnLoading, setIsWebAuthnLoading] = useState(false)
    
    const mfaChallenge = useMfaChallenge()
    const _mfaMethods = useMfaMethodsStore()
    const { toast } = useToast()
    const navigate = useNavigate()
    const { setUser, setTokens, clearMfa } = useAuthStore()
    const api = new EnterpriseWebAuthnAPI()
    
    // Update selected method when methods change
    useEffect(() => {
        if (hasWebAuthn && selectedMethod === 'totp' && !hasTotp) {
            setSelectedMethod('webauthn')
        } else if (hasTotp && selectedMethod === 'webauthn' && !hasWebAuthn) {
            setSelectedMethod('totp')
        } else if (hasWebAuthn && hasTotp && selectedMethod === 'totp') {
            // If both are available, default to webauthn
            setSelectedMethod('webauthn')
        }
    }, [hasTotp, hasWebAuthn, selectedMethod])

    const mfaSchema = z.object({
        code: z.string().min(6, t('auth.mfaPage.codeTooShort')).max(8, t('auth.mfaPage.codeTooLong')),
    })

    type MfaFormData = z.infer<typeof mfaSchema>

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<MfaFormData>({
        resolver: zodResolver(mfaSchema),
    })

    // Check if user needs to set up MFA methods
    // Priority 1: Check mfaNeedsSetup flag from login response (most reliable)
    // Priority 2: Check user.mfa.secret === null (backend sets null when secret doesn't exist)
    
    const needsTotpSetup = mfaNeedsSetup && (user?.mfa?.totpEnabled && user?.mfa?.secret === null)
    // Check WebAuthn setup needs - either from user object or from mfaMethods indicating webauthn
    // Also check if webauthnEnabled is true but credentials don't exist
    const needsWebAuthnSetup = 
        (user?.mfa?.webauthnEnabled && user?.mfa?.webauthnCredentialsExist === false) ||
        (user?.webauthnEnabled && !user?.mfa?.webauthnCredentialsExist) ||
        (mfaNeedsSetup && mfaMethods.includes('webauthn') && !needsTotpSetup)

    // Debug logging removed to prevent console spam

    const onSubmit = (data: MfaFormData) => {
        mfaChallenge.mutate({ code: data.code })
    }

    const handleWebAuthn = async () => {
        if (!isWebAuthnSupported()) {
            toast({
                title: 'WebAuthn Not Supported',
                description: 'Your browser does not support passkeys. Please use a modern browser.',
                variant: 'destructive',
            })
            return
        }

        setIsWebAuthnLoading(true)

        try {
            const email = user?.email || 'admin@backupscheduler.com'
            
            if (import.meta.env.DEV) {
                console.log('🔐 Starting passkey authentication for MFA:', email)
            }
            
            const options = await api.beginAuthentication(email)

            const credential = await authenticateEnterprisePasskey(options)
            
            if (import.meta.env.DEV) {
                console.log('🔐 Passkey credential received, completing MFA authentication')
            }

            const result = await api.completeAuthentication(credential, email)

            const responseData = result.data || result
            
            if (responseData?.accessToken && responseData?.refreshToken) {
                if (import.meta.env.DEV) {
                    console.log('🔐 Passkey MFA authentication successful, user ID:', responseData.user?.id)
                }
                setTokens(responseData.accessToken, responseData.refreshToken, 15 * 60)
                setUser(responseData.user)
                clearMfa()
                navigate('/')
                toast({
                    title: 'Login Successful',
                    description: 'You have been authenticated with your passkey.',
                })
            } else {
                throw new Error('Authentication failed: No tokens received')
            }
        } catch (error: any) {
            const errorMessage = getWebAuthnErrorMessage(error)
            toast({
                title: 'Passkey Authentication Failed',
                description: errorMessage,
                variant: 'destructive',
            })
        } finally {
            setIsWebAuthnLoading(false)
        }
    }
    
    // Note: Removed auto-trigger - user should explicitly click the button

    // If user needs to set up WebAuthn first, show the setup page
    // BUT only if MFA is actually required (not if user just logged in with passkey)
    if (needsWebAuthnSetup && !needsTotpSetup && mfaNeedsSetup) {
        return <WebAuthnSetupPage />
    }

    // If user needs to set up TOTP first, show the setup page
    if (needsTotpSetup) {
        return <TotpSetupPage />
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
            <div className="w-full max-w-md space-y-6">
                {/* Language selector */}
                <div className="flex justify-end">
                    <LanguageSwitcher />
                </div>

                {/* Header */}
                <div className="text-center space-y-4">
                    <div className="flex justify-center">
                        <Logo className="h-16" />
                    </div>
                    <div className="space-y-2">
                        <h1 className="text-2xl font-bold">{t('auth.mfaPage.title')}</h1>
                        <p className="text-muted-foreground">
                            {t('auth.mfaPage.subtitle')}
                        </p>
                    </div>
                </div>

                {/* MFA form */}
                <Card>
                    <CardHeader>
                        <CardTitle>{t('auth.mfaPage.verifyIdentity')}</CardTitle>
                        <CardDescription>
                            {t('auth.mfaPage.verifyDescription')}
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        {/* Method selection - show both if both are enabled */}
                        {(hasTotp && hasWebAuthn) && (
                            <div className="space-y-3">
                                <Label>{t('auth.mfaPage.authMethod')}</Label>
                                <div className="grid grid-cols-2 gap-2">
                                    <Button
                                        type="button"
                                        variant={selectedMethod === 'webauthn' ? 'default' : 'outline'}
                                        onClick={() => setSelectedMethod('webauthn')}
                                        className="justify-start"
                                    >
                                        <Key className="mr-2 h-4 w-4" />
                                        Passkey
                                    </Button>
                                    <Button
                                        type="button"
                                        variant={selectedMethod === 'totp' ? 'default' : 'outline'}
                                        onClick={() => setSelectedMethod('totp')}
                                        className="justify-start"
                                    >
                                        <Smartphone className="mr-2 h-4 w-4" />
                                        {t('auth.mfaPage.authenticatorApp')}
                                    </Button>
                                </div>
                            </div>
                        )}

                        {/* Passkey authentication */}
                        {selectedMethod === 'webauthn' && hasWebAuthn && (
                            <div className="space-y-4">
                                <div className="text-center space-y-2">
                                    <p className="text-sm text-muted-foreground">
                                        Use your passkey to authenticate
                                    </p>
                                    {isWebAuthnLoading && (
                                        <p className="text-sm text-muted-foreground">Waiting for passkey...</p>
                                    )}
                                </div>
                                {!isWebAuthnLoading && (
                                    <Button
                                        type="button"
                                        onClick={handleWebAuthn}
                                        className="w-full"
                                        disabled={isWebAuthnLoading}
                                    >
                                        <Key className="mr-2 h-4 w-4" />
                                        Authenticate with Passkey
                                    </Button>
                                )}
                            </div>
                        )}

                        {/* TOTP form */}
                        {selectedMethod === 'totp' && hasTotp && (
                            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="code">{t('auth.mfaPage.authCode')}</Label>
                                    <Input
                                        id="code"
                                        type="text"
                                        placeholder={t('auth.mfaPage.codePlaceholder')}
                                        maxLength={8}
                                        {...register('code')}
                                        className={cn(errors.code && 'border-destructive')}
                                        autoComplete="one-time-code"
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        autoCorrect="off"
                                        autoCapitalize="off"
                                        spellCheck={false}
                                    />
                                    {errors.code && (
                                        <p className="text-sm text-destructive">{errors.code.message}</p>
                                    )}
                                </div>

                                <Button
                                    type="submit"
                                    className="w-full"
                                    disabled={mfaChallenge.isPending}
                                >
                                    {mfaChallenge.isPending ? t('auth.mfaPage.verifying') : t('auth.mfaPage.verifyCode')}
                                </Button>
                            </form>
                        )}
                    </CardContent>
                </Card>

                {/* Footer */}
                <div className="text-center text-sm text-muted-foreground">
                    <p>{t('auth.mfaPage.secureAuthRequired')}</p>
                </div>
            </div>
        </div>
    )
}






















